<template>
  <div>
    <b-modal id="entryManagerModal" size="xl" content-class="menu-bg-blue-transparent-modal dpr-em-modal"
      body-class="scroll-Container" title="Daily Productivity Report Entry Manager" scrollable hide-footer
      @show="initDPREntryManager()">
      <b-container fluid>
        <MMP_QueryConstructor :comp="'DPR'" :fromdate="fromdate" :todate="todate" :isAdmin="isAdmin" :user="user"
          :getConditions="getConditions" @applyCustomQuery="applyCustomQuery($event)"></MMP_QueryConstructor>
        <b-row class="mx-1 mt-3"> Time Range </b-row>
        <!-- Date picker -->
        <b-row class="mx-2 my-3">
          <b-col class="col-sm-3">
            <label class="form-check-label">
              <input type="radio" class="form-check-input" name="optDateFilter" v-model="filterType" id="allDay"
                value="allDay" checked @change="dateInput()" />Single Day
            </label>
            <label class="form-check-label chk-align">
              <input type="radio" class="form-check-input" name="optDateFilter" v-model="filterType" id="dateRange"
                @change="dateInput()" value="range" />Date Range
            </label>
          </b-col>
          <b-col class="col-sm-8 form-check-inline align">
            <b-form-input class="form-control col-sm-3 propStyle" type="date" placeholder="From date"
              format="dd/MM/yyyy" @change="loadEntryManager()" v-model="fromdate"></b-form-input>
            <label v-if="filterType != 'allDay'" class="mx-3">TO</label>
            <b-form-input v-if="filterType != 'allDay'" class="form-control col-sm-3 propStyle" type="date"
              placeholder="To date" format="dd/MM/yyyy" @change="loadEntryManager()" v-model="todate"></b-form-input>
            <b-link @click="loadEntryManager(isAdmin)" class="btn-margin link" size="md">
              Filter
            </b-link>

            <b-link @click="loadMissingReports()" class="btn-margin link" size="md">
              Missing Entries
            </b-link>
          </b-col>
        </b-row>

        <vue-bootstrap4-table v-if="!entryManagerSpinner" :rows="entryManagerRows" :columns="entryManagerColumns"
          :config="config" :actions="isAdmin ? adminActions : actions" :classes="classes" @downloadCsv="downloadCsv()"
          @openDPR="initDprModal()">
          <template slot="Collection Day" slot-scope="props">
            <span>{{ formatDateForDisplay(props.row['Collection Day']) }}</span>
          </template>

          <template slot="Full Day Downtime Reason" slot-scope="props">
            <!-- Full Day Down Time -->
            <div v-if="props.row['Full Day Downtime Reason']" class="downDayHighlight">
              <span>
                <strong>Full Day: </strong>
                {{ props.row['Full Day Downtime Reason'] }}
              </span>
            </div>

            <!-- Partial Day Down Time -->
            <div v-if="props.row['Partial Day Downtime Reason']" class="partialDownDayHighlight">
              <fa v-if="isAdmin" :icon="['fas', 'window-close']" @click="clearPartialDowntime(props.row)"
                class="float-right m-1" style="cursor: pointer" title="Remove Partial Downtime Reason" size="sm" />
              <span>
                <strong>Partial Day: </strong>
                {{ props.row['Partial Day Downtime Reason'] }}
                ({{ props.row['hours_dt_partial'] }} hours)
              </span>
            </div>

            <!-- Reason Quick Edit Button -->
            <b-button v-if="!props.row['Partial Day Downtime Reason'] && isAdmin"
              @click="showReasonDropdown(props.row.vbt_id)" size="sm" variant="warning" class="mt-2">
              <fa :icon="['fas', 'pen-square']" size="lg" title="Update downtime" />
            </b-button>

            <!-- Down Time Quick Edit Dropdown -->
            <b-form-select v-if="reasonQuickEditManager[props.row.vbt_id - 1]" v-model="selectedDownTimeReason"
              @change="submitChanges(props.row)" id="partialDowntimeReason" class="mt-2">
              <option v-for="(reason, index) in downtimeReasons" :key="index" :value="{ reason: reason, index: index }">
                {{ reason }}
              </option>
            </b-form-select>
          </template>

          <template slot="Comments" slot-scope="props">
            <span class="comments-text">{{ props.row['Comments'] }}</span>
          </template>

          <template slot="validated" slot-scope="props">
            <b-form-checkbox v-model="props.row['validated']" :value="true" :unchecked-value="false" switch
              @change="toggleValidated(props.row)">
              <span :class="props.row['validated'] ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                {{ props.row['validated'] ? 'Yes' : 'No' }}
              </span>
            </b-form-checkbox>
          </template>

          <template slot="update" slot-scope="props">
            <fa :icon="['fas', 'pencil-alt']" size="lg" variant="primary" title="Update entry"
              @click="updateEntry(props.row)" style="cursor: pointer" />
          </template>

          <template v-if="isAdmin" slot="delete" slot-scope="props">
            <fa :icon="['fas', 'trash']" size="lg" @click="deleteEntry(props.row)" title="Delete entry"
              style="cursor: pointer" />
          </template>
        </vue-bootstrap4-table>
        <!-- Loading Spinner -->
        <div class="d-flex justify-content-center align-items-center py-1" v-if="entryManagerSpinner">
          <span class="spinner-border spinner-border-md highlightLoader" role="status" aria-hidden="true"></span>
          <span class="highlightLoader p-1 spinner-border-md">Loading Data...</span>
        </div>
      </b-container>
      <MMP_DPR_MissingEntriesModal :entries="missingEntries"></MMP_DPR_MissingEntriesModal>
    </b-modal>
  </div>
</template>

<style>
@media (min-width: 768px) {
  .modal-xl {
    width: 95%;
    max-width: 1600px;
  }
}

.date-column-width {
  min-width: 125px;
}

.downDayHighlight {
  background-color: rgba(255, 83, 83, 0.5);
}

.partialDownDayHighlight {
  background-color: rgba(217, 255, 93, 0.5);
}

.comments-text {
  font-size: 16px;
}

.align {
  margin-top: 0%;
}

.chk-align {
  margin-left: 32% !important;
}

.btn-margin {
  margin-left: 5%;
}

/* ── DPR Entry Manager Modal ───────────────────────────── */
.dpr-em-modal .modal-header {
  background: #063c6d;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 12px 20px;
  border-radius: 7px 7px 0 0;
}

.dpr-em-modal .modal-title {
  color: #fff;
  font-weight: 600;
  text-shadow: none;
}

.dpr-em-modal .close {
  color: #fff;
  opacity: 0.8;
  text-shadow: none;
}

/* Table: the VBT4 classes config puts menu-bg-blue-transparent-modal on the
   <table> element itself — reset it to transparent here */
.dpr-em-modal .menu-bg-blue-transparent-modal {
  background: transparent !important;
  color: #063c6d !important;
  text-shadow: none !important;
}

.dpr-em-modal table {
  color: #063c6d !important;
  text-shadow: none !important;
}

.dpr-em-modal thead th {
  background: rgba(6, 60, 109, 0.06) !important;
  color: #063c6d !important;
  border-color: rgba(6, 60, 109, 0.12) !important;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-shadow: none !important;
}

.dpr-em-modal thead th a,
.dpr-em-modal thead th svg {
  color: rgba(6, 60, 109, 0.5) !important;
}

.dpr-em-modal tbody tr {
  color: #063c6d !important;
  text-shadow: none !important;
}

.dpr-em-modal tbody tr:hover {
  background: rgba(6, 60, 109, 0.04) !important;
}

.dpr-em-modal tbody td {
  color: #063c6d !important;
  border-color: rgba(6, 60, 109, 0.08) !important;
  text-shadow: none !important;
}

.dpr-em-modal .footer-pagination-row td,
.dpr-em-modal .footer-pagination-row th {
  background: rgba(6, 60, 109, 0.06) !important;
  color: #063c6d !important;
  border-color: rgba(6, 60, 109, 0.12) !important;
  text-shadow: none !important;
}

/* Apply / Refresh buttons — header color, stays white on hover */
.dpr-em-modal .mmp-button {
  background-color: #063c6d !important;
  border-color: #063c6d !important;
  color: #fff !important;
}

.dpr-em-modal .mmp-button:hover {
  background-color: #0a5499 !important;
  border-color: #0a5499 !important;
  color: #fff !important;
  text-decoration: none !important;
}

/* Query constructor form controls */
.dpr-em-modal .propStyle,
.dpr-em-modal .form-control {
  background: #f3f5f8 !important;
  border: 1px solid rgba(6, 60, 109, 0.2) !important;
  color: #063c6d !important;
}

.dpr-em-modal .propStyle:focus,
.dpr-em-modal .form-control:focus {
  background: #fff !important;
  border-color: #063c6d !important;
  box-shadow: 0 0 0 2px rgba(6, 60, 109, 0.12) !important;
}

/* Filter / Missing Entries links */
.dpr-em-modal .link {
  color: #063c6d !important;
  font-weight: 600;
}

.dpr-em-modal .link:hover {
  color: #0a5499 !important;
  text-decoration: underline;
}

/* Add to Query link */
.dpr-em-modal .mx-3[href],
.dpr-em-modal a.mx-3 {
  color: #063c6d !important;
}
</style>

<script>
import MMP_DPR_MissingEntriesModal from './MMP_DPR_MissingEntriesModal.vue'
import MMP_QueryConstructor from '../shared/MMP_QueryConstructor.vue'
import VueBootstrap4Table from 'vue-bootstrap4-table'
import { addDays } from '../../../utils/utils.js'
import axios from 'axios'
import * as XLSX from 'xlsx'
const currentDate = new Date()
export default {
  name: 'MMP_DPR_EntryManager',
  components: {
    MMP_DPR_MissingEntriesModal,
    MMP_QueryConstructor,
    VueBootstrap4Table
  },
  props: ['dprUpdateInfo', 'isAdmin', 'user'],
  watch: {
    dprUpdateInfo: function (newValue, oldValue) {
      this.entryManagerRows.forEach(currentRow => {
        if (currentRow['id'] === newValue.id) {
          currentRow['Operator 1'] = this.operators[newValue.operator1_id]
          currentRow['Full Day Downtime Reason'] = newValue.reason_dt
          currentRow['Partial Day Downtime Reason'] = newValue.reason_dt_part
          currentRow.hours_dt_partial = newValue.hours_dt_partial
          currentRow.road_type = Array.isArray(newValue.road_type)
            ? newValue.road_type.map(entry => JSON.parse(entry).type).join(', ')
            : JSON.parse(newValue.road_type)
          currentRow.locations = [
            'United States of America',
            'Canada'
          ].includes(newValue.collected_location_country)
            ? (newValue.collected_location != null ?
              newValue.collected_location.map(entry => this.possibleLocations[entry])
                .join(', ')
              : '')
            : ''
          currentRow.hotel_location = newValue.hotel_location
          currentRow['Comments'] = newValue.comments
        }
      })
    }
  },
  data() {
    return {
      app: 'dpr',
      todate: null,
      fromdate: addDays(currentDate, 0),
      formSubmitErrors: [],
      filterType: 'allDay',
      getConditions: null,
      classes: {
        table: {
          'menu-bg-blue-transparent-modal': true
        }
      },
      // Numerical columns do not sort correctly
      entryManagerColumns: [
        {
          label: 'Jday',
          name: 'Jday',
          initial_sort: true,
          sort: true
        },
        {
          label: 'Collection Day',
          name: 'Collection Day',
          sort: false,
          column_classes: 'date-column-width'
        },
        {
          label: 'Vehicle',
          name: 'Vehicle',
          sort: true
        },
        {
          label: 'Operator 1',
          name: 'Operator 1',
          sort: false
        },
        {
          label: 'Down Time',
          name: 'Full Day Downtime Reason',
          sort: false
        },
        {
          label: 'Road Type',
          name: 'road_type',
          sort: false
        },
        {
          label: 'Collected Locations',
          name: 'locations',
          sort: false
        },
        {
          label: 'Hotel Location',
          name: 'hotel_location',
          sort: false
        },
        {
          label: 'Comments',
          name: 'Comments',
          sort: false
        },
        {
          label: 'Validated',
          name: 'validated',
          sort: false
        },
        {
          label: '',
          name: 'update',
          sort: false
        },
        {
          label: '',
          name: 'delete',
          sort: false
        }
      ],
      entryManagerRows: [],
      actions: [
        {
          btn_text: 'New Entry',
          event_name: 'openDPR',
          event_payload: {
            msg: 'Add new entry'
          },
          class: 'btn mmp-button d-none'
        }
      ],
      adminActions: [
        {
          btn_text: 'New Entry',
          event_name: 'openDPR',
          event_payload: {
            msg: 'Add new entry'
          },
          class: 'btn mmp-button'
        },
        {
          btn_text: 'Download',
          event_name: 'downloadCsv',
          event_payload: {
            msg: 'Download DPR CSV'
          },
          class: 'ml-4 btn btn-warning '
        }
      ],
      config: {
        checkbox_rows: false,
        rows_selectable: true,
        card_mode: false,
        global_search: {
          placeholder: 'Search...',
          visibility: true,
          showClearButton: true,
          searchOnPressEnter: true
        },
        highlight_row_hover_color: '#4a4a4a',
        per_page: 10,
        fromdate: { placeholder: 'Search...', visibility: true },
        todate: { placeholder: 'Search...', visibility: true },
        show_refresh_button: false,
        show_reset_button: false
      },
      //user: {org: '1'},
      entryManagerSpinner: false,
      updateEntryID: null,
      form: {
        date: null,
        vehicle_id: null,
        collectedLocation: [],
        operator1_id: null,
        operator2_id: null,
        operator3_id: null,
        truckHours: 0,
        newTruckHours: 0,
        officeHours: 0,
        gtps: null,
        gtpsDriveBy: null,
        gtpsName: null,
        selectedRoadTypes: [],
        fuelCost: 0,
        hotelCost: 0,
        maintenanceCost: 0,
        miscellaneousCost: 0,
        downtimeReason: 'NA',
        partialDowntimeReason: 'NA',
        numHoursPartialDowntime: 0,
        cameraUtilized: null,
        dmiStatus: null,
        comments: null
      },
      editBgColorChange: 'lightblue',
      downtimeReasons: [
        'NA',
        'Collect',
        'Weather',
        'Lidar Issues',
        'Routine Maintenance',
        'Non-Routine Maintenance',
        'Mobilization',
        'Sick',
        'Travel',
        'Not Sufficient Operators',
        'PTO',
        'Holiday',
        'System Setup',
        'At Riegl',
        'No System',
        'Review',
        'DB Trail Issue',
        '3DMS Project'
      ],
      selectedDownTimeReason: null,
      previouslySelectedDownTimeReason: null,
      reasonQuickEditManager: [],
      operators: {},
      vehicles: {},
      missingEntries: {},
      csv: 'data:text/csv;charset=utf-8,',
      possibleLocations: {
        // USA locations
        Alabama: 'AL',
        Alaska: 'AK',
        'American Samoa': 'AS',
        Arizona: 'AZ',
        Arkansas: 'AR',
        California: 'CA',
        Colorado: 'CO',
        Connecticut: 'CT',
        Delaware: 'DE',
        'District of Columbia': 'DC',
        'Federated States of Micronesia': 'FSM',
        Florida: 'FL',
        Georgia: 'GA',
        Guam: 'GU',
        Hawaii: 'HI',
        Idaho: 'ID',
        Illinois: 'IL',
        Indiana: 'IN',
        Iowa: 'IA',
        Kansas: 'KS',
        Kentucky: 'KY',
        Louisiana: 'LA',
        Maine: 'ME',
        'Marshall Islands': 'MH',
        Maryland: 'MD',
        Massachusetts: 'MA',
        Michigan: 'MI',
        Minnesota: 'MN',
        Mississippi: 'MS',
        Missouri: 'MO',
        Montana: 'MT',
        Nebraska: 'NE',
        Nevada: 'NV',
        'New Hampshire': 'NH',
        'New Jersey': 'NJ',
        'New Mexico': 'NM',
        'New York': 'NY',
        'North Carolina': 'NC',
        'North Dakota': 'ND',
        'Northern Mariana Islands': 'MP',
        Ohio: 'OH',
        Oklahoma: 'OK',
        Oregon: 'OR',
        Pennsylvania: 'PA',
        'Puerto Rico': 'PR',
        'Rhode Island': 'RI',
        'South Carolina': 'SC',
        'South Dakota': 'SD',
        Tennessee: 'TN',
        Texas: 'TX',
        Utah: 'UT',
        Vermont: 'VT',
        'Virgin Island': 'VI',
        Virginia: 'VA',
        Washington: 'WA',
        'West Virginia': 'WV',
        Wisconsin: 'WI',
        Wyoming: 'WY',
        // CAN locations
        Alberta: 'AB',
        'British Columbia': 'BC',
        Manitoba: 'MB',
        'New Brunswick': 'NB',
        'Newfoundland and Labrador': 'NL',
        'Northwest Territories': 'NT',
        'Nova Scotia': 'NS',
        Nunavut: 'NU',
        Ontario: 'ON',
        'Prince Edward Island': 'PE',
        Quebec: 'QC',
        Saskatchewan: 'SK',
        'Yukon Territory': 'YT'
      }
    }
  },
  methods: {
    initDPREntryManager() {
      this.loadEntryManager()
      this.getOperators()
      this.getDevices()
    },
    showReasonDropdown(id) {
      this.selectedDownTimeReason = null
      for (let i in this.entryManagerRows) {
        /* Add 1 to i because the passed in id is
        1-indexed while the array is 0-indexed. */
        this.reasonQuickEditManager[i] = parseInt(i) + 1 == id
      }
    },
    resetReasonQuickEditManager() {
      this.reasonQuickEditManager = []
      for (let i in this.entryManagerRows) {
        this.reasonQuickEditManager.push(false)
      }
    },
    clearPartialDowntime(row) {
      if (sessionStorage.token) {
        axios
          .get(
            process.env.API_LOCATION + '/api/dpr/removePartialDowntimeReason',
            {
              headers: {
                'x-auth-token': sessionStorage.token
              },
              params: {
                id: parseInt(row.id)
              }
            }
          )
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log(
                'Update downtime reason API call error: ' + content.errors
              )
              this.$bvToast.toast(
                'Error: Failed to remove partial downtime reason.',
                {
                  autoHideDelay: 7000,
                  title: 'Entry Manager Record Changed',
                  toaster: 'b-toaster-top-right'
                }
              )
            } else {
              this.loadEntryManager()
              this.$bvToast.toast('Removed partial downtime reason.', {
                autoHideDelay: 7000,
                title: 'Entry Manager Record Changed',
                toaster: 'b-toaster-top-right'
              })
            }
          })
          .catch(function (error) {
            console.log(error)
            this.$bvToast.toast(
              'Error: Failed to remove partial downtime reason.',
              {
                autoHideDelay: 7000,
                title: 'Entry Manager Record Changed',
                toaster: 'b-toaster-top-right'
              }
            )
          })
      } else {
        console.log('No User Token')
      }
    },
    submitChanges(row) {
      let downtimeReason = this.selectedDownTimeReason.reason
      let previousDowntimeReason

      this.previouslySelectedDownTimeReason = row['Full Day Downtime Reason']
        ? row['Full Day Downtime Reason']
        : null
      previousDowntimeReason = this.previouslySelectedDownTimeReason

      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/dpr/updateDowntimeReason', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              downtimeReason: downtimeReason,
              id: parseInt(row.id)
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log(
                'Update downtime reason API call error: ' + content.errors
              )
              this.$bvToast.toast('Error: Failed to update downtime reason.', {
                autoHideDelay: 7000,
                title: 'Entry Manager Record Changed',
                toaster: 'b-toaster-top-right'
              })
            } else {
              this.$bvToast.toast(
                'Changed reason' +
                (previousDowntimeReason
                  ? ' from ' + previousDowntimeReason
                  : '') +
                ' to ' +
                downtimeReason +
                '.',
                {
                  autoHideDelay: 7000,
                  title: 'Entry Manager Record Changed',
                  toaster: 'b-toaster-top-right'
                }
              )
              for (let i in this.entryManagerRows) {
                // Hides the quick edit dropdown after changing downtime reason
                this.reasonQuickEditManager[i] = false
              }
              row['Full Day Downtime Reason'] = downtimeReason
            }
          })
          .catch(function (error) {
            console.log(error)
            this.$bvToast.toast('Error: Failed to update downtime reason.', {
              autoHideDelay: 7000,
              title: 'Entry Manager Record Changed',
              toaster: 'b-toaster-top-right'
            })
          })
      } else {
        console.log('No User Token')
      }
    },
    loadEntryManager() {
      this.entryManagerSpinner = true
      if (this.filterType != 'allDay') {
        let fmdt = new Date(this.fromdate)
        let tdt = new Date(this.todate)
        if (fmdt > tdt) {
          this.$bvToast.toast('FromDate can not be greater than Todate', {
            autoHideDelay: 10000,
            title: 'Incorrect Date format',
            toaster: 'b-toaster-top-center'
          })
        }
        if (tdt > new Date()) {
          this.$bvToast.toast('ToDate can not be greater than the current date', {
            autoHideDelay: 10000,
            title: 'Incorrect Date format',
            toaster: 'b-toaster-top-center'
          })
        }
        if (fmdt > new Date()) {
          this.$bvToast.toast('FromDate can not be greater than the current date', {
            autoHideDelay: 10000,
            title: 'Incorrect Date format',
            toaster: 'b-toaster-top-center'
          })
        }
      }
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/dpr/entry_manager', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              fromdate: this.fromdate,
              todate: this.todate,
              isAdmin: this.isAdmin,
              extent: this.user.extent
            }
          })
          .then(response => {
            const content = response.data
            // If duplicate entry found, throw error and prevent submission
            if (content.errors) {
              console.log('Entry Manager API Call Error: ' + content.errors)
            } else {
              // Place retrieved data into rows display array for table
              this.entryManagerRows = content.rows
              this.entryManagerRows.forEach(row => {
                if (row.road_type) {
                  row.road_type = Array.isArray(row.road_type)
                    ? row.road_type
                      .map(entry => JSON.parse(entry).type)
                      .join(', ')
                    : JSON.parse(row.road_type).type
                }
                if (row.collected_location) {
                  row.locations = row.collected_location
                    .map(entry => this.possibleLocations[entry])
                    .join(', ')
                }
              })
              // Hide spinner which will display the entry data
              this.entryManagerSpinner = false
              this.resetReasonQuickEditManager()
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    loadMissingEntries() {
      let missing = this.entryManagerRows.reduce((missing, row) => {
        if (!row['Operator 1']) {
          missing.push(row)
        }
        return missing
      }, [])
      missing.forEach(entry => {
        if (Object.keys(this.missingEntries).includes(entry['Jday'])) {
          if (!this.missingEntries[entry['Jday']].includes(entry['Vehicle'])) {
            this.missingEntries[entry['Jday']].push(entry['Vehicle'])
          }
        } else {
          this.missingEntries[entry['Jday']] = [entry['Vehicle']]
        }
      })
    },
    loadMissingReports() {
      this.loadMissingEntries()
      this.$bvModal.show('missingEntriesModal')
    },
    closeEntryManager() {
      this.$bvModal.hide('entryManagerModal')
    },
    applyCustomQuery(queryRows) {
      this.entryManagerRows = queryRows.rows
      this.getConditions = queryRows.cond
      this.resetReasonQuickEditManager()
    },
    formatDateForField(date) {
      const newDate = new Date(date)
      return (
        newDate.getFullYear() +
        '-' +
        (newDate.getMonth() + 1).pad(2) +
        '-' +
        newDate.getDate().pad(2)
      )
    },
    updateEntry(row) {
      // Call parent method for referring to DPR component
      this.$parent.$parent.editDprModal(row)
    },
    deleteEntry(row) {
      const entryToDeleteID = row.id
      if (confirm('Deleting entry cannot be undone')) {
        axios
          .get(process.env.API_LOCATION + '/api/dpr/deleteEntry', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              id: entryToDeleteID
            }
          })
          .then(response => {
            // Remove entry from current view without reloading data
            for (let i = this.entryManagerRows.length - 1; i >= 0; i--) {
              if (this.entryManagerRows[i].id === entryToDeleteID) {
                this.entryManagerRows.splice(i, 1)
              }
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      }
    },
    toggleValidated(row) {
      if (!sessionStorage.token) {
        console.log('No User Token')
        return
      }
      axios
        .get(process.env.API_LOCATION + '/api/dpr/updateValidated', {
          headers: { 'x-auth-token': sessionStorage.token },
          params: { id: parseInt(row.id), validated: row['validated'] }
        })
        .then(response => {
          const content = response.data
          if (content.errors) {
            row['validated'] = !row['validated']
            this.$bvToast.toast('Error: Failed to update validated status.', {
              autoHideDelay: 5000,
              title: 'Entry Manager',
              toaster: 'b-toaster-top-right',
              variant: 'danger',
            })
          } else {
            this.$bvToast.toast(
              `Entry marked as ${row['validated'] ? 'Validated' : 'Not Validated'}.`,
              { autoHideDelay: 4000, title: 'Entry Manager', toaster: 'b-toaster-top-right', variant: 'success', }
            )
          }
        })
        .catch(() => {
          row['validated'] = !row['validated']
          this.$bvToast.toast('Error: Failed to update validated status.', {
            autoHideDelay: 5000,
            title: 'Entry Manager',
            toaster: 'b-toaster-top-right'
          })
        })
    },
    // Display date as mm-dd-yyyy
    formatDateForDisplay(date) {
      if (date === null) {
        return
      }
      const newDate = new Date(date)
      return (
        (newDate.getMonth() + 1).pad(2) +
        '-' +
        newDate.getDate().pad(2) +
        '-' +
        newDate.getFullYear()
      )
    },
    initDprModal() {
      this.$bvModal.show('DPRModal')
    },
    dateInput() {
      if (this.filterType == 'allDay') {
        this.fromdate = addDays(currentDate, 0)
        this.todate = null
      } else {
        this.fromdate = addDays(currentDate, -31)
        this.todate = addDays(currentDate, 0)
      }
      this.loadEntryManager()
    },
    getOperators() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/dpr/getActiveOperators', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              isAdmin: this.isAdmin,
              extent: this.user.extent
            }
          })
          .then(response => {
            const content = response.data
            // If duplicate entry found, throw error and prevent submission
            if (content.errors) {
              console.log('Operator API Call Error: ' + content.errors)
            } else {
              content.rows.forEach(operator => {
                this.operators[operator.id] = operator.name
              })
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    getDevices() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/dpr/allVehicles', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              isAdmin: this.isAdmin,
              extent: this.user.extent
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('Vehicles API Call Error: ' + content.errors)
            } else {
              content.forEach(vehicle => {
                this.vehicles[vehicle.id] = vehicle.vehicle_name
              })
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    downloadCsv() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/dpr/downloadDprCSV', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              fromdate: this.fromdate,
              todate: this.todate,
              extent: this.user.extent
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('DPR CSV API Call Error: ' + content.errors)
            } else {
              content.rows.forEach(element => {
                element.comments =
                  element.comments != null
                    ? element.comments
                      .split(',')
                      .join('.')
                      .split('\n')
                      .join('.')
                    : element.comments
                element.reason_dt =
                  element.reason_dt != null
                    ? element.reason_dt.replace(',', '.')
                    : element.reason_dt
                element.collected_location =
                  element.collected_location != null
                    ? element.collected_location.join(' & ')
                    : element.collected_location
              })

              this.csvExport(content.rows)
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    csvExport(parsedJson) {
      if (
        !Array.isArray(parsedJson) ||
        !parsedJson.every(p => typeof p === 'object' && p !== null)
      ) {
        return
      }

      // WEBGIS-2952
      // Preprocess the data to stringify JSONB columns
      parsedJson.forEach(row => {
        for (let key in row) {
          if (Array.isArray(row[key])) {
            let items;

            if (key == 'road_type') {
              items = row[key].map(item => JSON.parse(item).type)
            } else if (key == 'issues') {
              items = row[key].map(item => JSON.parse(item).issue)
            }

            row[key] = items.join(', ')
          }

          if ((key == 'created_date' || key == 'updated_date' || key == 'Collection Day') && row[key]) {
            row[key] = row[key].split('T')[0]
          }

        }
      });

      let data = XLSX.utils.json_to_sheet(parsedJson)
      let wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, data, 'DPR')
      let fileName = this.filterType == 'allDay' ? `DPR_Report_${this.fromdate}.xlsx` : `DPR_Report_${this.fromdate}_to_${this.todate}.xlsx`
      XLSX.writeFile(wb, fileName)
    }
  }
}
</script>
